import React from "react";
import { Helmet } from "react-helmet";
import { ROUTES } from "../../lib/constants";
import { Image } from "../../components/Image/Image";
import ConstructionMan from "../../images/construction-man.jpg";
import ConstructionPic1 from "../../images/construction-pic1.jpg";
import ConstructionPic2 from "../../images/construction-pic2.jpg";
import ConstructionRestoration from "../../images/street-restoration.jpg";
import ConstructionForm from "../ConstructionForm/ConstructionForm";
import { Video } from "../../components/Video/Video";

import "../SmartBiz/forms.scss";
import styles from "./Construction.module.scss";

export const Construction = (): React.JSX.Element => {
  // SAFE: resolve FAQs URL without crashing
  const faqsHref: string =
    (ROUTES as any)?.faqs?.path ?? (ROUTES as any)?.faqs ?? "/faqs";

  return (
    <>
      <Helmet>
        <title>{ROUTES.construction?.title ?? "Construction"}</title>
        <meta
          property="og:title"
          content={`${ROUTES.construction?.title ?? "Construction"}`}
        />
        <meta
          property="og:url"
          content="https://www.citysidefiber.com/construction"
        />
      </Helmet>

      {/* ======= HERO (optional image slot preserved, no desktop changes) ======= */}

      {/* ======= OUR CONSTRUCTION PROCESS ======= */}
      <section className={`${styles.constructionProcess} section`}>
        <div className="container">
          <h2>Our Construction Process</h2>

          <div className={`${styles.stepsWrap} grid-12`}>
            <div className={styles.image}>
              <div className="img rounded">
                <Image
                  sources={[{ src: ConstructionMan, breakpoint: "default" }]}
                  alt="Cityside construction worker operating equipment"
                />
              </div>
              <br />
              <div className="img rounded">
                <Image
                  sources={[
                    { src: ConstructionRestoration, breakpoint: "default" },
                  ]}
                  alt="Clean, smooth street restoration after micro-trenching"
                />
              </div>
            </div>

            <div className={styles.steps} role="list">
              <div className="step" role="listitem">
                <div className="label">Step #1</div>
                <h3>Design, Engineer and Permit</h3>
                <div className="step-text">
                  The development process kicks off with extensive planning,
                  design, and engineering, with the intention of bringing a
                  fiber network connection past every resident and business
                  within the city. Our team works with City staff to coordinate
                  permitting and a construction schedule that minimizes
                  disruption for residents as much as possible. After permits
                  are received, we will locate public utilities with removable
                  temporary markings in the streets, parkways, and sidewalks.
                </div>
              </div>

              <div className="step" role="listitem">
                <div className="label">Step #2</div>
                <h3>Construction</h3>
                <div className="step-text">
                  The construction process includes the installation of
                  underground conduit, fiber, and access vaults in the public
                  right of way adjacent to the road. Prior to construction, we
                  will deliver multiple notices by mail and in-person with
                  information on the process. When your street is ready to start
                  construction, we will notify residents ahead of time with door
                  hangers, no parking signs, and additional details. Throughout
                  the process, we will provide updates via social media, email,
                  construction signs, and mailers. The construction process is
                  designed to move quickly and safely through each neighborhood,
                  minimizing the impact on residents. Please contact our
                  concierge for a speedy resolution if you have any concerns.
                </div>
              </div>

              <div className="step" role="listitem">
                <div className="label">Step #3</div>
                <h3>Splice, Test, and Connect</h3>
                <div className="step-text">
                  Once the underground conduit and access vaults are in place,
                  our construction teams will install the fiber-optic cables and
                  connect them together to create one seamless network. The
                  network is then tested to ensure that it is up to our quality
                  standards for speed and reliability.
                </div>
              </div>

              <div className="step" role="listitem">
                <div className="label">Step #4</div>
                <h3>Street Restoration</h3>
                <div className="step-text">
                  Final street restoration is determined by each project. Typically, we will
                  install our network underground and fill the micro-trench with concrete slurry on the same day.
                  After 2-4 weeks, we will return to the area to fill the section above the
                  micro trench with an asphalt compound level to the existing roadway.
                  Sometimes the city plans to return later to coat the street with a slurry seal.
                  In other cases, the city has plans to install new roadway asphalt,
                  which is why we install our network before major street construction begins.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ======= CONNECTING YOUR HOME ======= */}
      <section className={`${styles.cardsSection} section`}>
        <div className="container ">
          <h2 className={styles.head}>Connecting Your Home</h2>

          <div className="cards-wrap">
            <div className="row grid-12">
              <div className="text">
                <div className="label">Step #1</div>
                <h3>Pre-Installation</h3>
                <div className="info">
                You can now sign up for Cityside Fiber services! Once you've set an installation date, our crew will stop by a few days in advance to prepare your home's customized installation. Then they will extend fiber from the right-of-way on your street or sidewalk area (what was laid during construction) up to the side of your home. While other providers will simply leave the wire laying on top of your landscaping for weeks at a time, we bury ours immediately. Doing this protects your internet connection and keeps your yard looking tidy!
                </div>
              </div>
              <div className="img">
                <Image
                  sources={[{ src: ConstructionPic2, breakpoint: "default" }]}
                  alt="Fiber installation materials and cable spool"
                />
              </div>
            </div>

            <div className="row grid-12">
              <div className="text">
                <div className="label">Step #2</div>
                <h3>Installation</h3>
                <div className="info">
                An installation technician will arrive to activate our fiber-optic network in your home. They do this by connecting fiber directly to a router which provides WiFi to the devices throughout your home. They will test the connection to make sure your speeds are as expected, show you how to connect devices to WiFi, and set up your Cityside MyCityWiFi App so you can manage your services from anywhere!
                </div>
              </div>
              <div className="img">
                <Image
                  sources={[{ src: ConstructionPic1, breakpoint: "default" }]}
                  alt="Technician handling fiber during installation"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ======= FAQ TEASER + FORM ======= */}
      <section
        className={styles.formBlock1}
        id="smartbiz-form"
        aria-labelledby="faqTeaserHeading"
      >
        <section className={styles.faqSection}>
          <div className={styles.faqContainer}>
            <h2 id="faqTeaserHeading">Want to know more?</h2>
            <p>Read our Construction Frequently Asked Questions.</p>
            <a 
              href="/ConstructionFAQs.pdf" 
              className={styles.faqButton}
              download="Construction-FAQs.pdf"
            >
              Construction Frequently Asked Questions
            </a>
            <p className={styles.contactNote}>
              If you have any concerns or questions, please reach out to{" "}
              <a href="mailto:construction@citysidefiber.com">
                construction@citysidefiber.com
              </a>
              .
              <br />
              To submit an inquiry please fill out the form below.
            </p>
          </div>
        </section>

        <div className={`${styles.formContainer} form-shell`}>
          <ConstructionForm />
        </div>
      </section>

      {/* ======= VIDEO CAROUSEL ======= */}
      <section
        className={styles.constructionVideosSection}
        aria-label="Construction Videos"
      >
        <div className="grid-12">
          <h2>Construction Videos</h2>
        </div>

        <div className={styles.constructionVideos}>
          <div className="video">
            <Video
              src="https://www.youtube.com/embed/lsGobRz9DAk?si=0sc2197Ug5YaINwq"
              poster="https://img.youtube.com/vi/lsGobRz9DAk/maxresdefault.jpg"
              iconColor="white"
            />
          </div>
          <div className="video">
            <Video
              src="https://www.youtube.com/embed/4DD40AG9nWI?si=frPySTF65NLL3_66"
              poster="https://img.youtube.com/vi/4DD40AG9nWI/maxresdefault.jpg"
              iconColor="white"
            />
          </div>
          <div className="video">
            <Video
              src="https://www.youtube.com/embed/jYSpMAcOMm8?si=gc3W-XjQnIuCwOEV"
              poster="https://img.youtube.com/vi/jYSpMAcOMm8/maxresdefault.jpg"
              iconColor="white"
            />
          </div>
          <div className="video">
            <Video
              src="https://www.youtube.com/embed/his2E4mm8lI?si=eJXIxlX2uwFS_UWp"
              poster="https://img.youtube.com/vi/his2E4mm8lI/maxresdefault.jpg"
              iconColor="white"
            />
          </div>
          <div className="video">
            <Video
              src="https://youtube.com/embed/EoRepSxZxTA?si=5U58a_UlOoyW-ll0"
              poster="https://img.youtube.com/vi/EoRepSxZxTA/maxresdefault.jpg"
              iconColor="white"
            />
          </div>
          <div className="video">
            <Video
              src="https://www.youtube.com/embed/dHiYqCmFUjQ?si=VAK_ISY2L6beP53r"
              poster="https://img.youtube.com/vi/dHiYqCmFUjQ/maxresdefault.jpg"
              iconColor="white"
            />
          </div>
          <div className="video">
            <Video
              src="https://www.youtube.com/embed/NnbXM5mtEZo?si=8FXPEO33lPGxmRMo"
              poster="https://img.youtube.com/vi/NnbXM5mtEZo/maxresdefault.jpg"
              iconColor="white"
            />
          </div>
        </div>
      </section>
    </>
  );
};

export default Construction;
